import { useEffect, useState } from 'react';
import { useActivation } from '@/contexts/ActivationContext';
import { useAcquirers } from '@/contexts/AcquirersContext';
import { httpClient } from '@/lib/http-client';
import { getEndpointUrl } from '@/lib/api-config';
import AcquirerGate from '@/components/adelantos/AcquirerGate';
import PacInstructivoCard from '@/components/settings/pac/PacInstructivoCard';
import PacCustomerCodeBadge from '@/components/settings/pac/PacCustomerCodeBadge';
import PacAccountRow, { type PacDetectedAccount } from '@/components/settings/pac/PacAccountRow';

const statusConfig = {
  active: {
    label: 'Activa',
    icon: 'check_circle',
    badgeClass: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    iconBoxClass: 'bg-accent-green/15 text-accent-green',
    message: 'Sincronizado. Las cuentas de abono detectadas aparecen abajo.',
    messageClass: 'text-accent-green',
  },
  syncing: {
    label: 'Sincronizando',
    icon: 'cloud_sync',
    badgeClass: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    iconBoxClass: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    message: 'Estamos cargando el historial de tus ventas. Esto puede tomar unos minutos.',
    messageClass: 'text-muted-foreground',
  },
  error: {
    label: 'Error',
    icon: 'error',
    badgeClass: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    iconBoxClass: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    message: 'Hubo un error al sincronizar la información con tu adquirente. Verifica tus credenciales e intenta nuevamente.',
    messageClass: 'text-red-600 dark:text-red-400',
  },
} as const;

export default function PagoAutomaticoCuentasPage() {
  const { refreshStatus, isStepComplete, isLoading: activationLoading } = useActivation();
  const { connections, isLoading: connectionsLoading, refresh: refreshConnections } = useAcquirers();
  const [accounts, setAccounts] = useState<PacDetectedAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [customerCode, setCustomerCode] = useState<string | null>(null);
  const [codeLoading, setCodeLoading] = useState(true);

  const hasAcquirer = isStepComplete('credencialesAdquirente');
  const anySyncing = connections.some((c) => c.status === 'syncing');

  const loadAccounts = async () => {
    try {
      const url = getEndpointUrl('pac', 'detected-accounts');
      const data = await httpClient.get<PacDetectedAccount[]>(url, { shouldCache: false });
      setAccounts(data);
    } catch {
      setAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  };

  const loadCustomerCode = async () => {
    try {
      const url = getEndpointUrl('pac', 'customer-code');
      const data = await httpClient.get<{ code: string }>(url, { shouldCache: false });
      setCustomerCode(data.code);
    } catch {
      setCustomerCode(null);
    } finally {
      setCodeLoading(false);
    }
  };

  useEffect(() => {
    if (!hasAcquirer) {
      setAccountsLoading(false);
      setCodeLoading(false);
      setAccounts([]);
      return;
    }
    void loadAccounts();
    void loadCustomerCode();
  }, [hasAcquirer]);

  useEffect(() => {
    if (!hasAcquirer) return;
    if (!anySyncing) return;
    const interval = setInterval(() => {
      void refreshStatus();
      void refreshConnections();
      void loadAccounts();
    }, 15_000);
    return () => clearInterval(interval);
  }, [hasAcquirer, anySyncing, refreshStatus, refreshConnections]);

  if (activationLoading) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <div className="flex items-center justify-center rounded-xl border bg-card p-12">
          <span className="material-symbols-outlined animate-spin text-3xl text-slate-300">progress_activity</span>
        </div>
      </div>
    );
  }

  if (!hasAcquirer) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <AcquirerGate
          title="Agrega un adquirente para ver tus cuentas detectadas"
          description="Para ver las cuentas de abono que detectamos, primero debes agregar al menos un adquirente en la configuración de tu cuenta."
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Pago automático de cuentas (PAC)</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Cuando terminas de registrar las credenciales de tu adquirente, disparamos automáticamente el sincronizador que lee los códigos de comercio y las cuentas de abono asociadas. Sobre cada
          cuenta puedes activar el mandato PAC para que cobremos automáticamente las cuotas adelantadas.
        </p>
      </div>

      <div className="rounded-xl border bg-card p-6 shadow-sm">
        <h2 className="text-lg font-medium">Estado de sincronización</h2>
        <p className="mt-1 text-xs text-muted-foreground">Mismo estado que ves en Credenciales Adquirente, una fila por conexión.</p>

        {connectionsLoading ? (
          <p className="mt-4 text-sm text-muted-foreground">Cargando…</p>
        ) : connections.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">Aún no hay adquirentes conectados.</p>
        ) : (
          <ul className="mt-4 space-y-3">
            {connections.map((conn) => {
              const cfg = statusConfig[conn.status];
              const isSyncing = conn.status === 'syncing';
              return (
                <li key={conn.id} className="flex items-start gap-4 rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                  <span className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg ${cfg.iconBoxClass}`}>
                    <span className={`material-symbols-outlined ${isSyncing ? 'animate-spin' : ''}`}>{isSyncing ? 'progress_activity' : cfg.icon}</span>
                  </span>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-medium">{conn.acquirerName}</h3>
                      <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${cfg.badgeClass}`}>{cfg.label}</span>
                      <span className="text-xs text-muted-foreground">RUT: {conn.rut}</span>
                    </div>
                    <p className={`mt-1 text-sm ${cfg.messageClass}`}>{cfg.message}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <PacInstructivoCard customerCode={customerCode} loading={codeLoading} />

      <div className="rounded-xl border bg-card p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-medium">Cuentas de abono</h2>
            <p className="mt-1 text-xs text-muted-foreground">Estas son las cuentas donde tu adquirente deposita las ventas. Activa el mandato PAC sobre cada una para autorizar el cobro automático.</p>
          </div>
          <PacCustomerCodeBadge code={customerCode} loading={codeLoading} />
        </div>

        {accountsLoading ? (
          <p className="mt-4 text-sm text-muted-foreground">Cargando…</p>
        ) : accounts.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">Todavía no hay cuentas detectadas.</p>
        ) : (
          <ul className="mt-4 divide-y divide-border">
            {accounts.map((acc) => (
              <PacAccountRow key={`${acc.codigoComercio}-${acc.rutTitular}-${acc.numeroCuenta}`} account={acc} customerCode={customerCode} onReceiptUploaded={() => void loadAccounts()} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
